/**
 * Service-worker registration.
 *
 * The worker itself is `public/sw.js` — plain JS, copied verbatim into `dist/`
 * by Vite and uploaded at the site root by deploy.sh's no-cache pass, so its
 * scope is the whole origin and a deploy always ships a fresh copy.
 *
 * It exists for three reasons, in this order:
 *  1. Chromium will not treat an app as installable without a worker that has
 *     a `fetch` handler.
 *  2. Offline tolerance for the shell itself — a cached `index.html` means a
 *     launched-from-the-homescreen app on a dead connection shows the shell
 *     rather than the browser's dinosaur.
 *  3. For a tenant whose modules are same-origin, offline tolerance for the
 *     APP: the module bundles and the resolve answer, so the launch lands on a
 *     working screen rather than empty chrome (DEV-1021).
 *
 * The tenant's policy travels in the worker's own SCRIPT URL. A fetch handler
 * has to decide synchronously whether to respond, so it cannot wait on a cache
 * read or a postMessage; the script URL is the one thing a worker knows before
 * its first event. Hence `/sw.js?tenant=<slug>&resolve=<url>[&offline=1]`.
 */
import { watchForUpdates } from "@/pwa/appUpdate";
import type { ResolveTenantAppResponse } from "@/tenant/types";

/**
 * The exact URL the shell fetched to resolve this tenant. The worker matches
 * this string, and only this string, so it must be built the same way
 * `fetchTenantApp` builds it (`tenant/resolve.ts`).
 */
export function resolveUrlFor(host: string, apiBase: string): string {
  return `${apiBase}/tenant-apps/resolve?host=${encodeURIComponent(host)}`;
}

/**
 * Whether this tenant's app can be made to work offline.
 *
 * The worker can only cache modules it is allowed to read and re-serve, which
 * means same-origin `/mfe/*`. A tenant with ANY module still on a per-module
 * CloudFront distribution would precache a partial set and launch offline into
 * a half-loaded app — worse than the honest empty shell, because it looks like
 * a bug in the module rather than an absent network.
 *
 * So the gate is all-or-nothing, and it is derived rather than configured:
 * nothing to flip per tenant, and no list to keep in step with the infra. It
 * answers false everywhere today and starts answering true, tenant by tenant,
 * as `/mfe/*` routing lands.
 *
 * An empty import map is not "offline-capable" — it is a tenant with no app.
 */
export function canRunOffline(
  imports: ReadonlyArray<{ url: string }>,
  origin: string = window.location.origin,
): boolean {
  if (!imports.length) return false;
  return imports.every((entry) => {
    try {
      return new URL(entry.url, origin).href.startsWith(origin + "/");
    } catch {
      return false;
    }
  });
}

/** `/sw.js` plus the tenant policy the worker reads out of its own script URL. */
export function serviceWorkerUrl(params: { tenant: string; resolveUrl: string; offline: boolean }): string {
  const query = new URLSearchParams({ tenant: params.tenant, resolve: params.resolveUrl });
  if (params.offline) query.set("offline", "1");
  return `/sw.js?${query.toString()}`;
}

export async function registerServiceWorker(options: {
  tenant: Pick<ResolveTenantAppResponse, "slug" | "importMap">;
  host: string;
  apiBase: string;
}): Promise<ServiceWorkerRegistration | null> {
  if (!("serviceWorker" in navigator)) return null;
  // A worker registered from `vite dev` would cache the dev server's module
  // graph and shadow every subsequent edit.
  if (!import.meta.env.PROD) return null;
  try {
    const url = serviceWorkerUrl({
      tenant: options.tenant.slug || "",
      resolveUrl: resolveUrlFor(options.host, options.apiBase),
      offline: canRunOffline(options.tenant.importMap?.imports ?? []),
    });
    // `updateViaCache: "none"` so the browser's HTTP cache can never hand back
    // a stale worker and pin the origin to a deleted build's asset names.
    const registration = await navigator.serviceWorker.register(url, { scope: "/", updateViaCache: "none" });
    // The worker parks an update rather than taking over; this is what notices
    // and offers the person the moment (pwa/appUpdate.ts).
    watchForUpdates(registration);
    return registration;
  } catch {
    // A blocked or unsupported registration costs the install prompt, nothing else.
    return null;
  }
}
